import React from "react";

export type CDNImageProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src"
> & {
  src: string | undefined | null;
  alt: string;
  width?: number;
  height?: number;
  loading?: "lazy" | "eager";
  /** The page's main image (LCP): fetch it first. Every other image loads
   *  lazily unless the caller passes `loading`. */
  priority?: boolean;
};

// Width ladder. Every transform width snaps UP to one of these buckets so a
// given source image is only ever transformed at a bounded set of widths.
// Cloudflare bills per unique (image, params) tuple, so collapsing arbitrary
// widths (56/64/72/88/96…) into shared buckets directly cuts the bill and caps
// growth as new call sites are added. The dominant existing widths (200/400/880)
// are ladder values, so this doesn't churn already-cached renditions.
const WIDTH_LADDER = [16, 32, 64, 96, 128, 200, 320, 400, 640, 880, 1280];

/** Snap a requested width up to the nearest ladder bucket (clamped to the max). */
export function snapImageWidth(width: number): number {
  return (
    WIDTH_LADDER.find((w) => width <= w) ??
    WIDTH_LADDER[WIDTH_LADDER.length - 1]
  );
}

// Retina handling for small images. At/below this DISPLAY width an image is
// almost always a small avatar or icon (message/contact avatars, pills, flags)
// that looks soft at 1× on hi-DPI screens but costs only a couple KB at 2×. We
// can't read devicePixelRatio at SSR/crawl time without a hydration mismatch,
// so we serve a fixed 2× for these and let the browser downscale. Larger widths
// — grid cards (200), about (400), hero (880) — stay 1× so the discovery grid
// and LCP images don't bloat, and the dominant billing buckets are untouched.
const RETINA_DOUBLE_MAX_WIDTH = 128;

/** Resolve a display width to the transform pixel width (2× for small avatars). */
export function resolveTransformWidth(displayWidth: number): number {
  const pixelWidth =
    displayWidth <= RETINA_DOUBLE_MAX_WIDTH ? displayWidth * 2 : displayWidth;
  return snapImageWidth(pixelWidth);
}

// Note: height/crop is intentionally not a CDN param. Display elements already
// constrain aspect via CSS (object-cover), and emitting `height,fit,gravity`
// only for some call sites produced duplicate billed transforms of the same
// image (e.g. `width=200` AND `width=200,height=200,...`). Width-only keeps one
// rendition per (image, width) bucket.
export function getCDNImageUrl(
  src: string | undefined | null,
  width: number,
): string {
  // No source yet (e.g. a freshly added Builder component) — nothing to transform.
  if (!src) {
    return "";
  }

  let finalSrc = src;

  // Skip CDN processing for data URLs (base64 images), Clerk images, and
  // Builder.io assets. Builder URLs (cdn.builder.io) aren't on the Cloudflare
  // image-resize allowlist, so wrapping them in the cdn-cgi/image prefix yields
  // a broken (unresized) URL — serve them directly instead.
  //
  // Match on the parsed hostname, not a substring, so a user-uploaded file
  // whose name merely contains one of these domains (e.g.
  // ".../my-clerk.com-avatar.jpg") still goes through normal processing.
  let isBypassHost = false;
  try {
    const { hostname } = new URL(finalSrc);
    isBypassHost = [
      "clerk.dev",
      "clerk.com",
      "img.clerk.com",
      "images.clerk.dev",
      "cdn.builder.io",
    ].some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch {
    // Relative path or otherwise unparseable — fall through to normal processing.
  }

  if (finalSrc.startsWith("data:") || isBypassHost) {
    return finalSrc;
  }

  // Replace Google Storage URL with form-data subdomain if present
  if (finalSrc.startsWith("https://storage.googleapis.com/dcm-form-data/")) {
    finalSrc = finalSrc.replace(
      "https://storage.googleapis.com/dcm-form-data/",
      "https://form-data.dreamcoachmatch.com/",
    );
  }

  // Prepend CDN URL
  return `https://dreamcoachmatch.com/cdn-cgi/image/width=${resolveTransformWidth(width)}/${finalSrc}`;
}

/** Image served through the Cloudflare resizer; use it for every remote image. */
export const CDNImage = (props: CDNImageProps) => {
  const { src, priority, ...rest } = props;

  // Avoid rendering an <img> with no source — it shows a broken-image icon and,
  // with a missing src, the browser would otherwise request the current page URL.
  if (!src) {
    return null;
  }

  const finalSrc = getCDNImageUrl(src, rest.width ?? 400);

  return (
    <img
      src={process.env.NODE_ENV === "development" ? src : finalSrc}
      {...(priority
        ? { loading: "eager", fetchPriority: "high" }
        : { loading: "lazy", decoding: "async" })}
      {...rest}
    />
  );
};
